import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Stack,
  Container,
  Tooltip,
  OutlinedInput,
} from "@mui/material";
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Close as CloseIcon,
  ThumbUp as ThumbUpIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  QuestionAnswer as RaplIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import {
  useGetRaplRequestsQuery,
  useUpdateRaplRequestMutation,
  useHideRaplRequestMutation,
  useShowRaplRequestMutation,
  useCloseRaplRequestMutation,
  type RaplRequest,
  type RaplRequestStatus,
} from "../../../entities/admin-raplrequests";
import { useGetMyPermissionsQuery } from "../../../entities/me";
import { ADMIN_PERMISSION_RAPL_REQUESTS } from "../../../shared/constants/admin-permission-ids";
import { ResidentCaliber } from "../../../entities/admin-events";
import {
  renderCaliberShortName,
  getCaliberColor,
} from "../../../shared/lib/caliber";
import { format, parseISO } from "date-fns";

const STATUS_LABELS: Record<RaplRequestStatus, string> = {
  actual: "Актуальный",
  completed: "Завершённый",
};

const STATUS_COLORS: Record<RaplRequestStatus, "success" | "default"> = {
  actual: "success",
  completed: "default",
};

export const RaplRequestsManagement = () => {
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<RaplRequestStatus | "">("");
  const [sortBy, setSortBy] = useState<"support" | "created" | "">("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [pageTokenHistory, setPageTokenHistory] = useState<
    (string | undefined)[]
  >([]);
  const [currentPageToken, setCurrentPageToken] = useState<string | undefined>(
    undefined,
  );

  const [selectedRequest, setSelectedRequest] = useState<RaplRequest | null>(
    null,
  );
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCloseDialog, setOpenCloseDialog] = useState(false);

  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editWhatTried, setEditWhatTried] = useState("");
  const [closeOutcome, setCloseOutcome] = useState("");

  const queryParams = useMemo(
    () => ({
      search: searchValue || undefined,
      status: (statusFilter || undefined) as RaplRequestStatus | undefined,
      sort: (sortBy || undefined) as "support" | "created" | undefined,
      order: sortOrder,
      pageToken: currentPageToken,
      limit,
    }),
    [searchValue, statusFilter, sortBy, sortOrder, currentPageToken, limit],
  );

  const { data: permissionsData, isLoading: isLoadingPermissions } =
    useGetMyPermissionsQuery();
  const canAccessRapl =
    permissionsData?.permissions?.includes(ADMIN_PERMISSION_RAPL_REQUESTS) ??
    false;

  const { data, isLoading } = useGetRaplRequestsQuery(queryParams, {
    skip: isLoadingPermissions || !canAccessRapl,
  });

  const [updateRequest, { isLoading: isUpdating }] =
    useUpdateRaplRequestMutation();
  const [hideRequest] = useHideRaplRequestMutation();
  const [showRequest] = useShowRaplRequestMutation();
  const [closeRequest, { isLoading: isClosing }] =
    useCloseRaplRequestMutation();

  const items = data?.items ?? [];
  const nextPageToken = data?.pageToken?.trim() || undefined;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    setPage(0);
    setPageTokenHistory([]);
    setCurrentPageToken(undefined);
  };

  const handleSortChange = (field: "support" | "created") => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(0);
    setPageTokenHistory([]);
    setCurrentPageToken(undefined);
  };

  const handleChangePage = useCallback(
    (_: unknown, newPage: number) => {
      if (newPage > page) {
        if (nextPageToken) {
          setPageTokenHistory((prev) => [...prev, currentPageToken]);
          setCurrentPageToken(nextPageToken);
          setPage(newPage);
        }
      } else if (newPage < page) {
        const prev = [...pageTokenHistory];
        const lastPageToken = prev.pop();
        setPageTokenHistory(prev);
        setCurrentPageToken(lastPageToken);
        setPage(newPage);
      }
    },
    [page, nextPageToken, currentPageToken, pageTokenHistory],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLimit(parseInt(event.target.value, 10));
      setPage(0);
      setPageTokenHistory([]);
      setCurrentPageToken(undefined);
    },
    [],
  );

  const handleOpenDetail = (req: RaplRequest) => {
    setSelectedRequest(req);
    setOpenDetailDialog(true);
  };

  const handleOpenEdit = (req: RaplRequest) => {
    setSelectedRequest(req);
    setEditTitle(req.title);
    setEditDescription(req.description);
    setEditWhatTried(req.whatTried);
    setOpenEditDialog(true);
  };

  const handleOpenClose = (req: RaplRequest) => {
    setSelectedRequest(req);
    setCloseOutcome(req.outcomeSummary ?? "");
    setOpenCloseDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenDetailDialog(false);
    setOpenEditDialog(false);
    setOpenCloseDialog(false);
  };

  const handleUpdate = async () => {
    if (!selectedRequest) return;
    try {
      await updateRequest({
        id: selectedRequest.id,
        body: {
          title: editTitle.trim(),
          description: editDescription.trim(),
          whatTried: editWhatTried.trim(),
        },
      }).unwrap();
      handleCloseDialogs();
    } catch (err) {
      console.error("Error updating RAPL request:", err);
    }
  };

  const handleToggleVisibility = async (req: RaplRequest) => {
    try {
      if (req.isHidden) {
        await showRequest(req.id).unwrap();
      } else {
        await hideRequest(req.id).unwrap();
      }
    } catch (err) {
      console.error("Error toggling visibility:", err);
    }
  };

  const handleClose = async () => {
    if (!selectedRequest) return;
    try {
      await closeRequest({
        id: selectedRequest.id,
        body: { outcomeSummary: closeOutcome.trim() },
      }).unwrap();
      handleCloseDialogs();
    } catch (err) {
      console.error("Error closing RAPL request:", err);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    try {
      return format(parseISO(dateStr), "dd.MM.yyyy HH:mm");
    } catch {
      return dateStr;
    }
  };

  const getAuthorName = (req: RaplRequest) => {
    const { firstName, lastName } = req.author;
    return `${firstName} ${lastName}`.trim() || "—";
  };

  if (isLoadingPermissions) {
    return (
      <Container sx={{ py: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!canAccessRapl) {
    return (
      <Container sx={{ py: 3 }}>
        <Typography color="text.secondary">
          Нет доступа к разделу «RAPL запросы».
        </Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <RaplIcon fontSize="large" color="primary" />
          <Typography fontWeight={600} fontSize={24}>
            RAPL запросы
          </Typography>
        </Box>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <ArrowUpIcon fontSize="small" />
              <ArrowDownIcon fontSize="small" />
              Нажмите на заголовки колонок для сортировки
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid sx={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                label="Поиск по заголовку и описанию"
                value={searchValue}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <SearchIcon />,
                }}
              />
            </Grid>
            <Grid sx={{ xs: 12, sm: 3, md: 2, minWidth: 180 }}>
              <FormControl fullWidth>
                <InputLabel>Статус</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as RaplRequestStatus | "");
                    setPage(0);
                    setPageTokenHistory([]);
                    setCurrentPageToken(undefined);
                  }}
                  input={<OutlinedInput label="Статус" />}
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="actual">Актуальные</MenuItem>
                  <MenuItem value="completed">Завершённые</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Заголовок</TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PersonIcon fontSize="small" />
                  Автор
                </Box>
              </TableCell>
              <TableCell
                sx={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSortChange("support")}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ThumbUpIcon fontSize="small" />
                  Поддержка
                  {sortBy === "support" &&
                    (sortOrder === "asc" ? (
                      <ArrowUpIcon fontSize="small" />
                    ) : (
                      <ArrowDownIcon fontSize="small" />
                    ))}
                </Box>
              </TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Видимость</TableCell>
              <TableCell
                sx={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => handleSortChange("created")}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CalendarIcon fontSize="small" />
                  Создан
                  {sortBy === "created" &&
                    (sortOrder === "asc" ? (
                      <ArrowUpIcon fontSize="small" />
                    ) : (
                      <ArrowDownIcon fontSize="small" />
                    ))}
                </Box>
              </TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Загрузка...
                </TableCell>
              </TableRow>
            ) : items.length > 0 ? (
              items.map((req) => (
                <TableRow
                  key={req.id}
                  hover
                  sx={{
                    cursor: "pointer",
                    opacity: req.isHidden ? 0.5 : 1,
                  }}
                  onClick={() => handleOpenDetail(req)}
                >
                  <TableCell>{req.id}</TableCell>
                  <TableCell sx={{ maxWidth: 250 }}>
                    <Typography noWrap variant="body2">
                      {req.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Avatar
                        src={req.author.avatarUrl}
                        sx={{ width: 28, height: 28 }}
                      >
                        {req.author.firstName?.[0]}
                      </Avatar>
                      <Typography variant="body2" noWrap>
                        {getAuthorName(req)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                      }}
                    >
                      <ThumbUpIcon
                        fontSize="small"
                        color={req.supportCount > 0 ? "success" : "action"}
                      />
                      {req.supportCount}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABELS[req.status]}
                      color={STATUS_COLORS[req.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {req.isHidden ? (
                      <Chip
                        label="Скрыт"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        label="Виден"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {formatDate(req.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Редактировать">
                        <IconButton
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEdit(req);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={req.isHidden ? "Показать" : "Скрыть"}>
                        <IconButton
                          color="default"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleVisibility(req);
                          }}
                        >
                          {req.isHidden ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      {req.status === "actual" && (
                        <Tooltip title="Закрыть запрос">
                          <IconButton
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenClose(req);
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Нет данных
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={-1}
          rowsPerPage={limit}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={() => ""}
        />
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog
        open={openDetailDialog}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
      >
        {selectedRequest && (
          <>
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" component="span">
                  Запрос #{selectedRequest.id}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={STATUS_LABELS[selectedRequest.status]}
                    color={STATUS_COLORS[selectedRequest.status]}
                    size="small"
                  />
                  {selectedRequest.isHidden && (
                    <Chip
                      label="Скрыт"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Автор
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Avatar
                      src={selectedRequest.author.avatarUrl}
                      sx={{ width: 36, height: 36 }}
                    >
                      {selectedRequest.author.firstName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body1">
                        {getAuthorName(selectedRequest)}
                      </Typography>
                      {selectedRequest.author.caliber ? (
                        <Chip
                          label={renderCaliberShortName(
                            selectedRequest.author.caliber as ResidentCaliber,
                          )}
                          color={getCaliberColor(
                            selectedRequest.author.caliber as ResidentCaliber,
                          )}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      ) : null}
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Заголовок
                  </Typography>
                  <Typography variant="body1">
                    {selectedRequest.title}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Описание
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {selectedRequest.description}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Что пробовали
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {selectedRequest.whatTried || "—"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 4 }}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Поддержка
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <ThumbUpIcon fontSize="small" color="action" />
                      <Typography>{selectedRequest.supportCount}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Создан
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(selectedRequest.createdAt)}
                    </Typography>
                  </Box>
                  {selectedRequest.closedAt && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Закрыт
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(selectedRequest.closedAt)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {selectedRequest.outcomeSummary && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Итог
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                      {selectedRequest.outcomeSummary}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  handleCloseDialogs();
                  handleToggleVisibility(selectedRequest);
                }}
                startIcon={
                  selectedRequest.isHidden ? (
                    <VisibilityIcon />
                  ) : (
                    <VisibilityOffIcon />
                  )
                }
              >
                {selectedRequest.isHidden ? "Показать" : "Скрыть"}
              </Button>
              <Button
                onClick={() => {
                  handleCloseDialogs();
                  handleOpenEdit(selectedRequest);
                }}
                startIcon={<EditIcon />}
              >
                Редактировать
              </Button>
              {selectedRequest.status === "actual" && (
                <Button
                  color="error"
                  onClick={() => {
                    handleCloseDialogs();
                    handleOpenClose(selectedRequest);
                  }}
                  startIcon={<CloseIcon />}
                >
                  Закрыть запрос
                </Button>
              )}
              <Button onClick={handleCloseDialogs}>Закрыть</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Редактирование запроса</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              fullWidth
              label="Заголовок"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
            />
            <TextField
              fullWidth
              label="Описание"
              multiline
              minRows={3}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
            <TextField
              fullWidth
              label="Что пробовали"
              multiline
              minRows={2}
              value={editWhatTried}
              onChange={(e) => setEditWhatTried(e.target.value)}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Отмена</Button>
          <Button
            onClick={handleUpdate}
            disabled={isUpdating || !editTitle.trim()}
            variant="contained"
          >
            {isUpdating ? <CircularProgress size={24} /> : "Сохранить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Dialog */}
      <Dialog
        open={openCloseDialog}
        onClose={handleCloseDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Закрыть запрос</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2} mt={1}>
            Запрос будет закрыт от имени администратора. Укажите итог /
            результат.
          </Typography>
          <TextField
            label="Итог"
            fullWidth
            multiline
            minRows={3}
            value={closeOutcome}
            onChange={(e) => setCloseOutcome(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Отмена</Button>
          <Button
            color="error"
            onClick={handleClose}
            disabled={isClosing}
            variant="contained"
          >
            {isClosing ? <CircularProgress size={24} /> : "Закрыть запрос"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
